import type { dashboard as DashboardNS } from '@cwos/api-client';
import { narrowedTo, populationNote } from '../figureState.js';

/**
 * ONE READING OF `QueueScope`, FOR EVERY DASHBOARD SURFACE THAT IS HANDED ONE.
 *
 * `QueueScope` — `organization | subset{programIds, caseload} | none{reason}` —
 * is the frame the dashboard domain puts on its own answers. It arrives on
 * `DocumentationSummary.scope`, on `ReconciliationSummary.scope`, and on
 * `TableData.rowScope` for every table widget. Three payloads, one type, and
 * before this file existed one of the three had a reading of it and the other
 * two rendered their figures bare.
 *
 * The reading lives here rather than in the first screen that needed it,
 * because the failure this closes is precisely a screen deciding for itself
 * what a narrowed answer looks like. A second reading would eventually disagree
 * with this one about the case that matters — `none`, where the numbers beside
 * the frame are not zeros — and the reader would have no way to tell which of
 * the two sentences they were being shown.
 *
 * NO NEW FRAME VOCABULARY. The subset arm goes through `figureState`'s
 * `populationNote`, so a narrowed queue figure says the same words as a narrowed
 * tile, a narrowed board and a narrowed rate. Nothing here spells a fourth
 * state; `QueueScope` has three and this file has three arms.
 */

/**
 * The noun phrase for a `subset` frame — what completes "Counts …".
 *
 * `QueueScope`'s two dimensions are not symmetrical and the sentence has to
 * respect that. `programIds` is a list of IDS: no dashboard surface has program
 * names to render and inventing "some programs" would be the exact failure
 * `readScope.ts` names — truthful and useless. So the program half is stated as
 * the reach it describes ("the programs you can see"), which is what the reader
 * can actually act on. The caseload half needs no list at all, because a
 * population derived from the reader's own supervisor edge is already
 * COMPLETELY described by naming that edge.
 *
 * `caseloadWho` IS THE CALLER'S, and it is the only part that varies between
 * these three surfaces. The edge is the same edge — `worker-scope.ts`'s "your
 * own worker row plus your direct reports" — but what is counted over it is
 * not: the documentation card counts paperwork, the reconciliation card counts
 * unmatched visits and time records, a table widget counts the workers
 * themselves. Naming it at the call site is the moment somebody has to notice
 * there was a choice, the same reason `statedPopulation` demands its `who`.
 *
 * The fourth combination — neither narrowing in force — cannot be sent:
 * `queueScope` (dashboard/service.ts) returns `organization` for it, and
 * `subset` promises "at least one of the two narrowings is in force". It is
 * still handled, because a total function beats a comment, and its sentence
 * asserts only that the figures are NOT the organization's — which is the one
 * thing `population: 'subset'` guarantees no matter which dimension caused it.
 */
/**
 * THE CASELOAD NOUN BELONGS TO THE PAYLOAD, NOT TO THE CARD.
 *
 * `caseloadWho` was written as a call-site decision, and while each payload had
 * exactly one surface reading it that was the same thing. It stopped being the
 * same thing the moment the landing screen's QUESTION TILES started reading the
 * frame too: `ServiceLevelSummary` is now read by the roster card in
 * `widgets.tsx` and by "Are staff hours on track?" in `RightNow.tsx`, and two
 * screens describing one narrowing in two sentences is the defect this file
 * exists to prevent, one level up.
 *
 * So the noun is fixed per payload and named here. What varies between payloads
 * is what they COUNT — paperwork, clients, alerts, workers — and that is
 * exactly what these differ on. `caseloadWho` stays a required parameter,
 * because the moment somebody adds a sixth surface they still have to say which
 * of these it counts rather than getting a default that is wrong for it.
 *
 * `ServiceLevelSummary` and `ReconciliationSummary` are not here: both count
 * workers over the supervisor edge, and `delivery.ts` already exports the
 * product's one sentence for that population as `OWN_CASELOAD`. A fifth
 * constant repeating it is how the delivery band and the reconciliation card
 * would drift apart.
 */

/** `DocumentationSummary` — the queue counts open paperwork. */
export const OPEN_PAPERWORK_CASELOAD = 'your own paperwork and your reports’';

/**
 * `CareGapSummary` — the rows are CLIENTS, so the noun is clients.
 *
 * The payload documents `caseload` as always false, because care-gap rows are
 * client-owned and the supervisor edge does not reach them. Named anyway: if
 * that edge ever does apply it will have narrowed to the clients on the far
 * side of it, and "you and the people you supervise" would be counting workers
 * on a card that counts nobody of the kind.
 */
export const CARE_GAP_CASELOAD = 'the clients you and the people you supervise serve';

/**
 * `ExceptionSummary` — the rows are alerts.
 *
 * Always `organization` today: `alert` carries no program or site column, so
 * nothing narrows the roll-up and this never renders. It exists so that the day
 * something does narrow it — a program column, a tenant-scoped feed — the
 * summary is not the last surface still framing a subset as everybody.
 */
export const EXCEPTION_CASELOAD = 'alerts about you and the people you supervise';

export function queueSubsetWho(
  programIds: readonly string[] | null,
  caseload: boolean,
  caseloadWho: string,
): string {
  if (caseload && programIds !== null) return `${caseloadWho}, within the programs you can see`;
  if (caseload) return caseloadWho;
  if (programIds !== null) return 'the programs you can see';
  return 'what your account reaches';
}

/**
 * WHOSE FIGURES THESE ARE — or null when they are the organization's and need
 * no defending.
 *
 * THREE ARMS, THREE DIFFERENT SENTENCES:
 *
 *   organization — nothing. The numbers are what they appear to be, and a note
 *                  on every card is a note nobody reads.
 *   subset       — `figure.counts`, the phrasing every other narrowed figure in
 *                  this product uses, via `populationNote`. One vocabulary.
 *   none         — the SERVER'S OWN SENTENCE, verbatim. It is written once, in
 *                  `queueScope`, and it already ends "These are not zeros." —
 *                  the same words these cards' `failedMessage` reaches for. The
 *                  reason a reader was shown nobody (no worker record and no
 *                  reports, versus a grant this view cannot express) is decided
 *                  server-side and is not a screen's to guess at.
 *
 * AND A FOURTH ARM THIS DOCBLOCK USED TO FORBID (F161). The old reasoning —
 * "a payload that arrived always carries one; the field is required on all
 * three types" — was true only in the type system: the api-client CASTS these
 * payloads, it does not parse them, so a scope-less summary (server bug,
 * version skew between a deployed API and this bundle) reached this function
 * and crashed the whole widget card at `scope.population`. A crash is the
 * worst possible rendering of an unstated frame.
 *
 * The absent-scope arm renders a SENTENCE, not silence, and that choice is the
 * point: in this function `null` already means "the organization's — the
 * numbers are what they appear to be", which is the one thing an absent frame
 * must never be taken for. (`NARROWING_UNKNOWN` renders nothing over in
 * `FilterSummary`, and correctly, because its summary still states what is
 * shown; here silence is the widest claim on offer.) The figures themselves
 * still render — they arrived — under words that decline to say whose they
 * are. "In flight" remains the caller's skeleton, exactly as before: this arm
 * is for a payload that ARRIVED wrong, not for one that has not arrived.
 */
export const QUEUE_FRAME_UNSTATED =
  'This data did not say whose figures these are — read them as a count of something, not necessarily of everything.';

export function queueScopeNote(
  scope: DashboardNS.QueueScope | undefined,
  caseloadWho: string,
): string | null {
  if (!scope || typeof scope.population !== 'string') return QUEUE_FRAME_UNSTATED;
  switch (scope.population) {
    case 'organization':
      return null;
    case 'subset':
      return populationNote(
        narrowedTo(queueSubsetWho(scope.programIds, scope.caseload, caseloadWho)),
      );
    case 'none':
      return scope.reason;
  }
  // Unreached when the union above is honest; reached when the server minted a
  // population this bundle predates. Same answer as no frame at all.
  return QUEUE_FRAME_UNSTATED;
}
